import React, { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  MenuItem, Pagination, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import api from 'utils/api'
import AdminBusinessSelect from './AdminBusinessSelect'
import SearchField from './SearchField'
import useListQuery from './useListQuery'
import { errorMessage, operationKey, timestamp, useAdminData } from './shared'

const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0))
const typeOptions = [
  ['charge_credit', 'Card funding'], ['auto_charge_credit', 'Automatic card funding'],
  ['coupon_credit', 'Promotional credit'], ['debit', 'Card-funded spend'], ['debit_coupon', 'Promotional spend'],
]
const dateKeys = ['from', 'to']
const typeLabel = value => typeOptions.find(([key]) => key === value)?.[1] || value?.replaceAll('_', ' ') || '—'

function IssuePromotionalCreditDialog({ onClose, onSuccess }) {
  const [businessId, setBusinessId] = useState('')
  const [amount, setAmount] = useState('10.00')
  const [reason, setReason] = useState('')
  const [key, setKey] = useState(operationKey)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const amountNumber = Number(amount)
  const amountValid = /^\d+(\.\d{1,2})?$/.test(amount) && amountNumber >= 0.01 && amountNumber <= 100
  const update = setter => value => { setter(value); setKey(operationKey()) }
  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const result = await api.post(`/admin/businesses/${businessId}/promotional-credits`,
        { amount: amountNumber, reason: reason.trim() }, { headers: { 'Idempotency-Key': key } })
      onSuccess(result)
    } catch (requestError) {
      setError(requestError)
    } finally { setBusy(false) }
  }
  return <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
    <DialogTitle>Issue promotional credit</DialogTitle>
    <DialogContent><Stack spacing={2.5} pt={1}>
      <Typography color="text.secondary">This adds HUG promotional credit. It does not add card-funded credit or create a Stripe charge.</Typography>
      <AdminBusinessSelect value={businessId} onChange={update(setBusinessId)} error={!businessId} helperText={!businessId ? 'Choose an active Business.' : ''} />
      <TextField label="Promotional credit amount" type="number" value={amount} onChange={event => update(setAmount)(event.target.value)}
        inputProps={{ min: 0.01, max: 100, step: 0.01 }} error={Boolean(amount) && !amountValid}
        helperText="Enter an amount from $0.01 to $100.00." />
      <TextField label="Reason" value={reason} onChange={event => update(setReason)(event.target.value)} multiline rows={3}
        inputProps={{ maxLength: 1000 }} required helperText={`${reason.length}/1000 characters`} />
      {error && <Alert severity="error">{errorMessage(error)}</Alert>}
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="contained" onClick={submit}
      disabled={busy || !businessId || !amountValid || !reason.trim()}>{busy ? 'Issuing…' : 'Issue promotional credit'}</Button></DialogActions>
  </Dialog>
}

export default function Credits() {
  const { params, setParams, change, changeDate, requestQuery, timeZone } = useListQuery({ size: '20', sort: 'transactionDatetime,desc' }, dateKeys)
  const { data, error, isLoading, mutate } = useAdminData(`/admin/credits?${requestQuery}`)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const page = Number(params.get('page') || 0)
  const clear = () => setParams({})
  return <Stack spacing={2.5}>
    {feedback && <Alert severity="success" onClose={() => setFeedback(null)}>{feedback}</Alert>}
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2}>
      <Typography color="text.secondary">Review financial activity without combining card-funded and promotional balances.</Typography>
      <Button variant="contained" onClick={() => setDialogOpen(true)}>Issue promotional credit</Button>
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      {[['Card-funded balance', data?.balances?.cardFunded], ['Promotional balance', data?.balances?.promotional]].map(([label, value]) =>
        <Paper key={label} variant="outlined" sx={{ p: 2.5, flex: 1 }}><Typography color="text.secondary">{label}</Typography><Typography variant="h5" mt={0.5}>{money(value)}</Typography></Paper>)}
    </Stack>
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
        <SearchField label="Search Business, reason or reference" value={params.get('q') || ''} onChange={value => change('q', value)} />
        <AdminBusinessSelect allowAll label="All Businesses" value={params.get('businessId') || ''} onChange={value => change('businessId', value)} />
        <TextField select size="small" label="Transaction type" value={params.get('type') || ''} onChange={event => change('type', event.target.value)}>
          <MenuItem value="">All types</MenuItem>{typeOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Exact amount" type="number" value={params.get('amount') || ''} onChange={event => change('amount', event.target.value)} inputProps={{ step: 0.01 }} />
        <TextField size="small" label="From date" type="date" InputLabelProps={{ shrink: true }} value={params.get('from') || ''} onChange={event => changeDate('from', event.target.value)} />
        <TextField size="small" label="Through date" type="date" InputLabelProps={{ shrink: true }} value={params.get('to') || ''} onChange={event => changeDate('to', event.target.value)} />
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" mt={2} gap={1}>
        <Typography variant="caption" color="text.secondary">Date filters and transaction times use your browser timezone ({timeZone}).</Typography>
        {Boolean(params.toString()) && <Button size="small" onClick={clear}>Clear filters</Button>}
      </Stack>
    </Paper>
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack px={2.5} py={2} direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography fontWeight={600}>{isLoading ? 'Loading transactions…' : `${data?.totalElements ?? 0} transactions`}</Typography>
        <TextField select size="small" label="Sort by" value={params.get('sort') || 'transactionDatetime,desc'} onChange={event => change('sort', event.target.value)} sx={{ minWidth: 180 }}>
          {[['transactionDatetime,desc', 'Newest first'], ['transactionDatetime,asc', 'Oldest first'], ['businessName,asc', 'Business A–Z'], ['amount,desc', 'Highest amount']].map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </TextField>
      </Stack>
      {isLoading && <LinearProgress aria-label="Loading transactions" />}
      {error ? <Alert severity="error" sx={{ m: 2 }}>{errorMessage(error)}</Alert> : !isLoading && <>
        <TableContainer><Table sx={{ minWidth: 880, '& th': { bgcolor: 'grey.50', color: 'text.secondary' } }}><TableHead><TableRow>
          {['Date', 'Business', 'Type', 'Amount', 'Reason or reference'].map(label => <TableCell key={label} align={label === 'Amount' ? 'right' : 'left'}>{label}</TableCell>)}
        </TableRow></TableHead><TableBody>
          {data?.content?.map(row => <TableRow hover key={row.transactionId}><TableCell>{timestamp(row.transactionDatetime)}</TableCell>
            <TableCell>{row.businessName || `Business ${row.businessId}`}<Typography variant="body2" color="text.secondary">ID {row.businessId}</Typography></TableCell>
            <TableCell>{typeLabel(row.type)}</TableCell><TableCell align="right">{money(row.amount)}</TableCell><TableCell>{row.reason || row.reference || '—'}</TableCell></TableRow>)}
          {!data?.content?.length && <TableRow><TableCell colSpan={5} align="center"><Box py={4}><Typography>No matching transactions.</Typography><Typography variant="body2" color="text.secondary" mt={1}>Try another search or clear the filters.</Typography></Box></TableCell></TableRow>}
        </TableBody></Table></TableContainer>
        <Stack px={2.5} py={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="body2" color="text.secondary">{data?.totalElements ? `${page * 20 + 1}–${Math.min((page + 1) * 20, data.totalElements)} of ${data.totalElements}` : '0 transactions'}</Typography>
          <Pagination count={Math.max(1, data?.totalPages || 1)} page={page + 1} onChange={(_, value) => change('page', String(value - 1))} />
        </Stack>
      </>}
    </Paper>
    {dialogOpen && <IssuePromotionalCreditDialog onClose={() => setDialogOpen(false)} onSuccess={() => {
      setDialogOpen(false); setFeedback('Promotional credit issued.'); mutate()
    }} />}
  </Stack>
}
